import * as React from 'react'
import { CircleIcon } from 'lucide-react'
import { CheckIcon, ChevronRightIcon } from '@/src/icons'
import { ContextMenu as ContextMenuPrimitive } from '@base-ui/react/context-menu'
import { Menu as MenuPrimitive } from '@base-ui/react/menu'

import { cn } from '@/registry/reapit/lib/utils'

// ---------------------------------------------------------------------------
// ContextMenu — migrated from Radix to Base UI
// ---------------------------------------------------------------------------
// Structural changes:
//   Radix: ContextMenu.Root > ContextMenu.Portal > ContextMenu.Content
//   Base UI: ContextMenu.Root > Menu.Portal > Menu.Positioner > Menu.Popup
//
// ContextMenu.Root and ContextMenu.Trigger are specific to context-menu.
// All other parts (Popup, Item, Group, etc.) come from the shared Menu primitive.
//
// Sub-component renames:
//   Content     → Positioner + Popup (baked into ContextMenuContent wrapper)
//   Label       → GroupLabel
//   Sub         → SubmenuRoot
//   SubTrigger  → SubmenuTrigger
//   SubContent  → Portal > Positioner > Popup (baked into ContextMenuSubContent)
//   ItemIndicator → CheckboxItemIndicator / RadioItemIndicator
//
// CSS variable renames:
//   --radix-context-menu-content-available-height → --available-height
//   --radix-context-menu-content-transform-origin → --transform-origin
//
// Data attribute changes:
//   data-[state=open/closed] → data-[open] / data-[starting-style] / data-[ending-style]
//   data-[state=open] on SubTrigger → data-[popup-open]
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ContextMenu (Root)
// ---------------------------------------------------------------------------

function ContextMenu({
  onOpenChange,
  ...props
}: Omit<React.ComponentProps<typeof ContextMenuPrimitive.Root>, 'onOpenChange'> & {
  onOpenChange?: (open: boolean) => void
}) {
  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      onOpenChange?.(open)
    },
    [onOpenChange],
  )

  return (
    <ContextMenuPrimitive.Root
      data-slot="context-menu"
      onOpenChange={onOpenChange ? handleOpenChange : undefined}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// ContextMenuTrigger
// ---------------------------------------------------------------------------

function ContextMenuTrigger({ ...props }: React.ComponentProps<typeof ContextMenuPrimitive.Trigger>) {
  return <ContextMenuPrimitive.Trigger data-slot="context-menu-trigger" {...props} />
}

// ---------------------------------------------------------------------------
// ContextMenuGroup
// ---------------------------------------------------------------------------

function ContextMenuGroup({ ...props }: React.ComponentProps<typeof MenuPrimitive.Group>) {
  return <MenuPrimitive.Group data-slot="context-menu-group" {...props} />
}

// ---------------------------------------------------------------------------
// ContextMenuPortal
// ---------------------------------------------------------------------------

function ContextMenuPortal({ ...props }: React.ComponentProps<typeof MenuPrimitive.Portal>) {
  return <MenuPrimitive.Portal data-slot="context-menu-portal" {...props} />
}

// ---------------------------------------------------------------------------
// ContextMenuSub (SubmenuRoot)
// ---------------------------------------------------------------------------

function ContextMenuSub({
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.SubmenuRoot>, 'children'> & {
  children?: React.ReactNode
}) {
  return <MenuPrimitive.SubmenuRoot data-slot="context-menu-sub" {...props} />
}

// ---------------------------------------------------------------------------
// ContextMenuRadioGroup
// ---------------------------------------------------------------------------

function ContextMenuRadioGroup({
  onValueChange,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.RadioGroup>, 'onValueChange'> & {
  onValueChange?: (value: string) => void
}) {
  const handleValueChange = React.useCallback(
    (value: unknown) => {
      onValueChange?.(String(value ?? ''))
    },
    [onValueChange],
  )

  return (
    <MenuPrimitive.RadioGroup
      data-slot="context-menu-radio-group"
      onValueChange={onValueChange ? handleValueChange : undefined}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// ContextMenuSubTrigger
// ---------------------------------------------------------------------------

function ContextMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.SubmenuTrigger>, 'render'> & {
  inset?: boolean
  children?: React.ReactNode
}) {
  return (
    <MenuPrimitive.SubmenuTrigger
      data-slot="context-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "flex cursor-default items-center rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[inset]:pl-8 data-[popup-open]:bg-accent data-[popup-open]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto" />
    </MenuPrimitive.SubmenuTrigger>
  )
}

// ---------------------------------------------------------------------------
// ContextMenuSubContent
// ---------------------------------------------------------------------------
// Bakes in Portal > Positioner > Popup for the sub-menu.

function ContextMenuSubContent({
  className,
  children,
  ...props
}: {
  className?: string
  children?: React.ReactNode
} & Omit<React.ComponentProps<typeof MenuPrimitive.Popup>, 'className' | 'children'>) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner>
        <MenuPrimitive.Popup
          data-slot="context-menu-sub-content"
          className={cn(
            // DS: radius-lg (8 px) to match other floating menu surfaces.
            // Base UI: --transform-origin replaces --radix-context-menu-content-transform-origin
            'z-50 min-w-[8rem] origin-[var(--transform-origin)] overflow-hidden rounded-lg border bg-popover p-1 text-popover-foreground shadow-lg outline-hidden transition-[opacity,scale] duration-150 scale-100 opacity-100 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
            className,
          )}
          {...props}
        >
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

// ---------------------------------------------------------------------------
// ContextMenuContent
// ---------------------------------------------------------------------------
// Bakes in Portal > Positioner > Popup so consumers keep the same simple API.

function ContextMenuContent({
  className,
  children,
  ...props
}: {
  className?: string
  children?: React.ReactNode
} & Omit<React.ComponentProps<typeof MenuPrimitive.Popup>, 'className' | 'children'>) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner>
        <MenuPrimitive.Popup
          data-slot="context-menu-content"
          className={cn(
            // DS: radius-lg (8 px), bg-popover surface, --border, shadow-md.
            // Base UI: --available-height, --transform-origin replace Radix CSS vars
            'z-50 max-h-[var(--available-height)] min-w-[8rem] origin-[var(--transform-origin)] overflow-x-hidden overflow-y-auto rounded-lg border bg-popover p-1 text-popover-foreground shadow-md outline-hidden transition-[opacity,scale] duration-150 scale-100 opacity-100 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
            className,
          )}
          {...props}
        >
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

// ---------------------------------------------------------------------------
// ContextMenuItem
// ---------------------------------------------------------------------------

function ContextMenuItem({
  className,
  inset,
  variant = 'default',
  onSelect,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.Item>, 'render'> & {
  inset?: boolean
  variant?: 'default' | 'destructive'
  /** Radix compat — called when the item is selected */
  onSelect?: () => void
  children?: React.ReactNode
}) {
  return (
    // DS: py-2 (8 px) for comfortable item height. Icons at size-4 (icon-sm = 16 px).
    <MenuPrimitive.Item
      data-slot="context-menu-item"
      data-inset={inset}
      data-variant={variant}
      onClick={onSelect}
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-sm px-2 py-2 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 data-[variant=destructive]:text-destructive data-[variant=destructive]:data-[highlighted]:bg-destructive/10 data-[variant=destructive]:data-[highlighted]:text-destructive dark:data-[variant=destructive]:data-[highlighted]:bg-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground data-[variant=destructive]:*:[svg]:text-destructive!",
        className,
      )}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// ContextMenuCheckboxItem
// ---------------------------------------------------------------------------

function ContextMenuCheckboxItem({
  className,
  children,
  checked,
  onCheckedChange,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.CheckboxItem>, 'render' | 'onCheckedChange'> & {
  children?: React.ReactNode
  onCheckedChange?: (checked: boolean) => void
}) {
  const handleCheckedChange = React.useCallback(
    (newChecked: boolean) => {
      onCheckedChange?.(newChecked)
    },
    [onCheckedChange],
  )

  return (
    <MenuPrimitive.CheckboxItem
      data-slot="context-menu-checkbox-item"
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-sm py-2 pr-2 pl-8 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      checked={checked}
      onCheckedChange={onCheckedChange ? handleCheckedChange : undefined}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <MenuPrimitive.CheckboxItemIndicator keepMounted={false}>
          <CheckIcon className="size-4" />
        </MenuPrimitive.CheckboxItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  )
}

// ---------------------------------------------------------------------------
// ContextMenuRadioItem
// ---------------------------------------------------------------------------

function ContextMenuRadioItem({
  className,
  children,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.RadioItem>, 'render'> & {
  children?: React.ReactNode
}) {
  return (
    <MenuPrimitive.RadioItem
      data-slot="context-menu-radio-item"
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-sm py-2 pr-2 pl-8 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <MenuPrimitive.RadioItemIndicator keepMounted={false}>
          <CircleIcon className="size-2 fill-current" />
        </MenuPrimitive.RadioItemIndicator>
      </span>
      {children}
    </MenuPrimitive.RadioItem>
  )
}

// ---------------------------------------------------------------------------
// ContextMenuLabel
// ---------------------------------------------------------------------------

function ContextMenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<'div'> & {
  inset?: boolean
}) {
  // Base UI's GroupLabel requires a parent Menu.Group context.
  // Radix allowed standalone labels outside groups. We use a plain div
  // to preserve the existing API while avoiding the context error.
  return (
    <div
      role="group"
      data-slot="context-menu-label"
      data-inset={inset}
      className={cn('px-2 py-1.5 text-sm font-medium text-foreground data-[inset]:pl-8', className)}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// ContextMenuSeparator
// ---------------------------------------------------------------------------

function ContextMenuSeparator({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      role="separator"
      data-slot="context-menu-separator"
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// ContextMenuShortcut
// ---------------------------------------------------------------------------

function ContextMenuShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="context-menu-shortcut"
      className={cn('ml-auto text-xs tracking-widest text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuCheckboxItem,
  ContextMenuRadioItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuGroup,
  ContextMenuPortal,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuRadioGroup,
}
